import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Image } from "primereact/image";
import localeText from "../../../../locale";
import { formatPrice } from "../../../../utilities/formatPrice";
import AdminService from "../../../../service/AdminService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { buildSearchText, formatDay, getToken, normalizeText } from "../../utils/adminUtils";
import SearchInput from "../../components/list/SearchInput";
import EmptyState from "../../components/list/EmptyState";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import "../AdminOrders/AdminOrders.css";

const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)(\?.*)?$/i;

// Pedidos pagos por transferência bancária aguardando confirmação do admin.
// Como no admin clássico, a lista vem sem checagem de messageCode e na ordem inversa.
const loadPendingOrders = async () => {
  const res = await new AdminService().getPendingOrders(getToken());
  return [...(res?.result || [])].reverse();
};

const receiptOf = (order) => order.payments?.[0]?.banktransferReceipt;

const downloadReceipt = async (fileUrl) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileUrl.split("/").pop()?.split("?")[0] || "receipt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error fetching the file:", error);
  }
};

const AdminPendingOrders = ({ userInfo }) => {
  const { items, setItems, loading, loadError, reload } = useAdminList(loadPendingOrders);
  const { run, runningId } = useAdminAction();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const rows = useMemo(
    () =>
      items.map((order) => ({
        ...order,
        amountLabel: `${order.itens?.[0]?.product?.currency || ""} ${formatPrice(
          Number(order.amountTotal) || 0,
        )}`.trim(),
        searchText: buildSearchText([
          String(order.id ?? ""),
          order.buyer?.username,
          order.buyer?.firstname,
          order.buyer?.surname,
        ]),
      })),
    [items],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    return term ? rows.filter((row) => row.searchText.includes(term)) : rows;
  }, [rows, search]);

  const confirmSelected = async () => {
    const order = selected;
    const ok = await run(
      order.id,
      async () =>
        expectCode(
          await new AdminService().confirmOrderBankTransfer(getToken(), userInfo, order.id),
          200000,
        ),
      "paymentConfirmed",
    );
    setSelected(null);
    // Como no admin clássico, o pedido confirmado sai da lista sem recarregar
    if (ok) setItems((prev) => prev.filter((item) => item.id !== order.id));
  };

  const receiptTemplate = (order) => {
    const receipt = receiptOf(order);
    if (!receipt) return <span className="admin-muted">—</span>;

    return (
      <span className="admin-pending__receipt">
        {IMAGE_EXTENSIONS.test(receipt) && (
          <Image src={receipt} alt={localeText("receipt")} preview />
        )}
        <a
          className="admin-icon-btn"
          href={receipt}
          target="_blank"
          rel="noopener noreferrer"
          title={localeText("receipt")}
          aria-label={localeText("receipt")}
        >
          <i className="pi pi-external-link" aria-hidden="true" />
        </a>
        <button
          type="button"
          className="admin-icon-btn"
          onClick={() => downloadReceipt(receipt)}
          title={localeText("adminPendingDownloadReceipt")}
          aria-label={localeText("adminPendingDownloadReceipt")}
        >
          <i className="pi pi-download" aria-hidden="true" />
        </button>
      </span>
    );
  };

  const hasFilters = !!search.trim();

  return (
    <div className="admin-list">
      <ConfirmActionDialog
        visible={!!selected}
        message={localeText("adminPendingConfirmMessage")}
        details={
          selected
            ? [
                { label: localeText("adminOrdersOrder"), value: `#${selected.id}` },
                {
                  label: localeText("buyer"),
                  value: selected.buyer?.username && `@${selected.buyer.username}`,
                },
                { label: localeText("totalAmount"), value: selected.amountLabel },
                { label: localeText("date"), value: formatDay(selected.dateTransaction) },
              ]
            : []
        }
        confirmLabel={localeText("transferConfirm")}
        loading={!!selected && runningId === selected.id}
        onConfirm={confirmSelected}
        onCancel={() => setSelected(null)}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("adminPendingSearchPlaceholder")}
          />
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminBankTransferPending")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            dataKey="id"
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            removableSort
            scrollable
            tableStyle={{ minWidth: "56rem" }}
            emptyMessage={
              loadError ? (
                <EmptyState
                  icon="pi pi-exclamation-triangle"
                  message={localeText("adminListLoadError")}
                  actionLabel={localeText("adminUsersRetry")}
                  onAction={reload}
                />
              ) : (
                <EmptyState
                  icon={hasFilters ? "pi pi-inbox" : "pi pi-check-circle"}
                  message={localeText(hasFilters ? "adminListNoResults" : "noPendingOrders")}
                  actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
                  onAction={() => setSearch("")}
                />
              )
            }
          >
            <Column
              header={localeText("adminOrdersOrder")}
              field="id"
              body={(order) => <span className="admin-strong">#{order.id}</span>}
              sortable
            />
            <Column
              header={localeText("buyer")}
              field="buyer.username"
              body={(order) => (
                <div className="admin-stack">
                  <span>
                    {`${order.buyer?.firstname || ""} ${order.buyer?.surname || ""}`.trim() ||
                      order.buyer?.username ||
                      "—"}
                  </span>
                  {order.buyer?.username && (
                    <span className="admin-muted">@{order.buyer.username}</span>
                  )}
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("totalAmount")}
              field="amountTotal"
              body={(order) => <span className="admin-num admin-strong">{order.amountLabel}</span>}
              sortable
            />
            <Column
              header={localeText("date")}
              field="dateTransaction"
              body={(order) => formatDay(order.dateTransaction)}
              sortable
            />
            <Column header={localeText("receipt")} body={receiptTemplate} />
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(order) => (
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  onClick={() => setSelected(order)}
                  disabled={runningId === order.id || !userInfo}
                >
                  <i
                    className={runningId === order.id ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    aria-hidden="true"
                  />
                  {localeText("transferConfirm")}
                </button>
              )}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminPendingOrders;
